import {
  Center,
  Spinner,
  Table,
  TableContainer,
  TableContainerProps,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

interface CustomTableProps<TData = any> extends Partial<TableOptions<TData>> {
  columns: ColumnDef<TData, any>[];
  data: TData[];
  isLoading?: boolean;
  isEmpty?: boolean;
  containerProps?: TableContainerProps;
}

const ReactTable = <TData,>({
  columns,
  data,
  enableRowSelection,
  isLoading,
  isEmpty,
  containerProps,
  ...rest
}: CustomTableProps<TData>) => {
  const { t } = useTranslation();
  const table = useReactTable<TData>({
    data,
    columns,
    state: {},
    defaultColumn: {
      size: -1,
      minSize: -1,
    },
    enableRowSelection: enableRowSelection || false,
    getCoreRowModel: getCoreRowModel(),
    ...rest,
  });

  const rowHoverColor = 'bg.canvas';
  const theadBackgroundColor = 'bg.canvas';

  return (
    <TableContainer
      pos={'relative'}
      overflowX={'auto'}
      overflowY={'auto'}
      minH={data.length <= 5 ? '500px' : 'auto'}
      maxH={600}
      bg='var(--chakra-colors-chakra-body-bg)'
      isolation={'isolate'}
      {...containerProps}
    >
      {isLoading && (
        <Center
          pos={'absolute'}
          zIndex={10}
          bg='var(--chakra-colors-chakra-body-bg)'
          opacity={0.5}
          cursor={'progress'}
          inset={0}
        >
          <Spinner size='xl' />
        </Center>
      )}

      {isEmpty && !isLoading && !data.length && (
        <Center pos={'absolute'} zIndex={50} inset={0}>
          <Text color='text.muted' fontSize='lg'>
            {t('common.noItemAvailable')}
          </Text>
        </Center>
      )}

      <Table>
        <Thead
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 2,
          }}
        >
          {table.getHeaderGroups().map((headerGroup) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <Th
                    key={header.id}
                    sx={{
                      width: header.getSize() === -1 ? 'auto' : `${header.getSize()}px`,
                      paddingInline: 4,
                      bg: theadBackgroundColor,
                      color: 'text.subtle',
                      fontSize: 'xs',
                      fontWeight: '600',
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      borderColor: 'border.default',
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </Th>
                );
              })}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {table.getRowModel().rows.map((row) => (
            <Tr
              key={row.id}
              _hover={{
                cursor: 'pointer',
                bg: rowHoverColor,
              }}
            >
              {row.getVisibleCells().map((cell) => (
                <Td
                  key={cell.id}
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    paddingInline: 4,
                    color: 'text.default',
                    borderColor: 'border.default',
                  }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export default ReactTable;
